import { useEffect, useRef, useState } from 'react';
import { BRAND } from './brand';
import { AppHeader } from './components/AppHeader';
import { Footer } from './components/Footer';
import { IconAlert, IconCheck, IconCopy, IconExternal } from './components/Icons';
import { ModalsProvider } from './components/modals';
import { Toasts } from './components/Toasts';
import { Alert, TopBand } from './components/ui';
import { useMarket, usePrices, useUser } from './data/queries';
import { Dashboard } from './pages/Dashboard';
import { Faucet } from './pages/Faucet';
import { History } from './pages/History';
import { Markets } from './pages/Markets';
import { Reserve } from './pages/Reserve';
import { Status } from './pages/Status';
import { navigate, useRoute } from './router';
import { useWallet } from './wallet/WalletProvider';

type Theme = 'light' | 'dark';
/** Light unless the visitor chose dark in the settings menu: the OS preference is deliberately not followed. */
const initialTheme = (): Theme => {
  try { return localStorage.getItem('rwalend:theme') === 'dark' ? 'dark' : 'light'; } catch { return 'light'; }
};

function CopyAddress({ address }: { address: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button type="button" className="btn btn--outlined btn--sm" onClick={() => { void navigator.clipboard?.writeText(address).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }); }}>
      {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}{copied ? 'Copied' : 'Copy address'}
    </button>
  );
}

function Pages() {
  const route = useRoute();
  const { address, status, settled, wrongNetwork, switchNetwork, walletKind } = useWallet();
  const market = useMarket();

  // Visitors without a wallet land on Markets. Only the first page view is redirected: choosing Dashboard from the
  // navigation still shows the connect prompt, and connecting later brings the dashboard back at the same URL.
  const landed = useRef(false);
  useEffect(() => {
    if (landed.current || !settled) return;
    landed.current = true;
    if (route.name === 'dashboard' && status !== 'connected') navigate('/markets', { replace: true });
  }, [settled, status, route.name]);
  const user = useUser(address);
  const prices = usePrices('7d');

  const banner = wrongNetwork && (
    <div className="page page--flush"><Alert tone="warning" action={<button type="button" className="btn btn--outlined btn--sm" onClick={() => void switchNetwork().catch(() => undefined)}>Switch network</button>}>
      <strong>Wrong network.</strong> This market lives on Robinhood Chain Testnet (chain 46630).</Alert></div>
  );
  // A wallet created at sign-in starts empty: without test ETH the first transaction cannot pay for gas, and on the
  // testnet the only source of ETH (and of stock tokens) is the official faucet.
  const noGas = walletKind === 'embedded' && status === 'connected' && !wrongNetwork && address && user.data?.nativeBalance === 0n && (
    <div className="page page--flush"><Alert tone="info" action={<span className="alert__actions"><CopyAddress address={address} /><a className="btn btn--outlined btn--sm" href={BRAND.faucetUrl} target="_blank" rel="noopener noreferrer">Official faucet <IconExternal size={12} /></a></span>}>
      <strong>Your new wallet has no ETH for gas.</strong> Copy your address, request test ETH and stock tokens from the official faucet, then come back.</Alert></div>
  );
  let page;
  if (route.name === 'status') page = <Status />;
  else if (route.name === 'history') page = <History />;
  else if (!market.data) {
    page = (
      <><TopBand title={<div className="market-title"><div><h1>&nbsp;</h1></div></div>} />
        <main className="page">{market.isError
          ? <section className="paper connect-card"><IconAlert size={28} /><h2>Market data unavailable</h2><p className="muted">Neither the API nor the chain could be reached.</p><button type="button" className="btn btn--outlined" onClick={() => void market.refetch()}>Retry</button></section>
          : <div className="skeleton-stack" aria-busy="true" aria-label="Loading"><div className="skeleton" style={{ height: 200 }} /><div className="skeleton" style={{ height: 320 }} /></div>}
        </main></>
    );
  } else if (route.name === 'markets') page = <Markets market={market.data} prices={prices.data} />;
  else if (route.name === 'reserve') page = <Reserve market={market.data} symbol={route.symbol} user={user.data} />;
  else if (route.name === 'faucet') page = <Faucet market={market.data} user={user.data} />;
  else page = <Dashboard market={market.data} user={user.data} prices={prices.data} />;

  return <>{banner}{noGas}{page}<Footer market={market.data} /></>;
}

export function App() {
  const [theme, setTheme] = useState<Theme>(initialTheme);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    // mobile browsers tint their own toolbar with this: match the dark header band of the active theme
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme === 'dark' ? '#080e0c' : '#0d1d18');
  }, [theme]);
  const toggleTheme = () => setTheme((v) => { const n = v === 'dark' ? 'light' : 'dark'; try { localStorage.setItem('rwalend:theme', n); } catch { /* private mode */ } return n; });
  return (
    <ModalsProvider>
      <p className="testnet-banner">Public testnet. Tokens here have no value and the market can be reset at any time.</p>
      <AppHeader theme={theme} onToggleTheme={toggleTheme} />
      <Pages />
      <Toasts />
    </ModalsProvider>
  );
}
